<template>
  <div
    class="img"
    :class="{ 'img--no-stretch': noStretch }"
    :style="{
      minWidth: minSize,
      minHeight: minSize,
    }"
    role="img"
  >
    <img
      ref="image"
      :class="{ 'img--loaded': !loading }"
      :alt="alt"
    />
    <div
      v-if="loading"
      class="img-loader absolute-full"
    >
      <svg
        :width="loaderSize"
        :height="loaderSize"
        viewBox="0 0 60 60"
      >
        <circle
          cx="30"
          cy="30"
          r="26"
          fill="none"
          stroke="#bbb"
          stroke-width="8"
        ></circle>
        <circle
          class="loader__tab"
          cx="30"
          cy="30"
          r="26"
          fill="none"
          stroke="currentColor"
          stroke-width="8"
        ></circle>
      </svg>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LImage',
  props: {
    src: {
      type: String,
      default: '',
    },
    alt: {
      type: String,
      default: '',
    },
    /**
     * Por padrão as imagens expandem pra todo container.
     * Para evitar isso deve-se colocar largura/altura específicas ou então ativar este campo
     * para que a imagem fique com seu tamanho original
     */
    noStretch: {
      type: Boolean,
      default: false,
    },
    /**
     * Tamanho do loader (não é necessário unidade).
     * Reduzir em casos de imagens menores. No máximo 8px a menos que o tamanho da imagem.
     * Ex: imagem de 50px, loaderSize máximo seria 42px.
     */
    loaderSize: {
      type: String,
      default: '48',
    },
  },
  data() {
    return {
      loading: true,
    };
  },
  computed: {
    minSize() {
      const loaderSizeNumber = Number(this.loaderSize.replace(/\D/g, ''));
      const padding = loaderSizeNumber / 2;

      return this.loading ? `${loaderSizeNumber + padding}px` : undefined;
    },
  },
  watch: {
    src(newSrc) {
      // Quando o src é alterado mas o lazy load já rodou, a imagem não carregaria.
      // para isso precisamos setar o src manualmente. Apenas editor.
      this.$refs.image.src = newSrc;
    },
  },
  mounted() {
    if (window.IntersectionObserver) {
      this.createObserver();
    } else {
      this.loadImage();
    }
  },
  methods: {
    loadImage() {
      const el = this.$refs.image;

      if (el) {
        el.src = this.src;

        el.addEventListener('load', () => {
          this.loading = false;
        });

        el.addEventListener('error', () => {
          this.loading = false;
        });
      }
    },
    createObserver() {
      const verticalThreshold = '120%'; // margem relativa em que as imagens começam a carregar
      const options = {
        root: null,
        rootMargin: `0px 0px ${verticalThreshold} 0px`,
        threshold: '0',
      };

      const observer = new IntersectionObserver(this.handleIntersect, options);
      observer.observe(this.$refs.image);
    },
    handleIntersect(entries, observer) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.loadImage();
          observer.unobserve(this.$refs.image);
        }
      });
    },
  },
};
</script>

<style scoped lang="scss">
.img {
  display: inline-block;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    max-width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0; // não exibir até carregar completamente
    transition: opacity 0.25s ease-in;

    &.img--loaded {
      opacity: 1;
    }
  }

  &--no-stretch {
    width: auto;
    height: auto;

    img {
      width: auto;
      height: auto;
    }
  }

  &-loader {
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      vertical-align: middle;
      color: var(--color, $gray-70);
      transform-origin: center center;
      animation: loader-spin 2s linear infinite;

      circle {
        stroke: $primary-140;
        opacity: 0.2;
      }

      .loader__tab {
        opacity: 1;
        stroke-linecap: round;
        stroke-dasharray: 1, 200;
        stroke-dashoffset: 0;
        animation: loader-dash 1.5s ease-in-out infinite;
      }
    }
  }
}

@keyframes loader-spin {
  0% {
    transform: rotate3d(0, 0, 1, 0deg);
  }
  25% {
    transform: rotate3d(0, 0, 1, 90deg);
  }
  50% {
    transform: rotate3d(0, 0, 1, 180deg);
  }
  75% {
    transform: rotate3d(0, 0, 1, 270deg);
  }
  100% {
    transform: rotate3d(0, 0, 1, 360deg);
  }
}

@keyframes loader-dash {
  0% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -36px;
  }
  100% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -162px;
  }
}
</style>
